import React, {
  useState, useCallback, useEffect, useRef, useImperativeHandle, useLayoutEffect,
} from 'react';
import { v4 as uuid } from 'uuid';
import { FaSearch, FaEraser } from 'react-icons/fa';

import { Typography } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  ActionsButtons,
  BottomGrid,
  ClearButtonHistorico,
  ExpandButton,
  SearchButton,
  HistoricoGrid,
  Form,
  FieldsContainer,
  VisualizarAPartirDeContainer,
} from './HistoricoStyles';
import DialogNew from '../../../layouts/FormDialog/DialogNew';
import TooltipUI from '../../../components/UI/Tooltip/Tooltip';
import DialogExpandirHistorico from './DialogExpandirHistorico';

import { HistoricoProcesso } from '../../../components/UI/HistoricoProcesso/HistoricoProcesso';

import CheckboxUI from '../../../components/UI/Field/Checkbox/CheckboxField';
import DateField from '../../../components/UI/Field/Date/DateField';
import InputField from '../../../components/UI/Field/Input';
import Grid24 from '../../../components/UI/Grid24/Grid24';
import SelectorField from '../../../components/UI/Field/Selector/SelectorField';
import { clearErrorField } from '../../../helpers/utils/form.utils';

import { inspecionarProcessoPageController as controller } from '../inspecaoProcessoController';
import { loadingInspecaoProcessoController as loadingController } from '../loadingInspecaoProcessoController';

import AlertDialog from '../../../components/UI/Dialogs/AlertDialog/AlertDialog';


function HistoricoComponent({
  processo,
  tiposHistorico = [],
  autores = [],
  fases = [],
  numeroDias,
  setLoadings,
  podeModificarFase = false,
}, ref) {
  const historicoRef = useRef();
  const formRef = useRef();

  const [historicos, setHistoricos] = useState({});
  const [dropdowns, setDropdowns] = useState(controller.makeDefaultDropdownHistorico());

  const [dataCadastramento, setDataCadastramento] = useState('');

  const [visualizarDataCadastramento, setVisualizarDataCadastramento] = useState({ key: uuid(), value: false });
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [warningDialog, setWarningDialog] = useState({ open: false, message: '' });
  const [errors, setErrors] = useState(controller.makeDefaultHistoricoSearchErrors());
  const [onOpenExpandirHistorico, setOnOpenExpandirHistorico] = useState(false);


  function moveScrollToTop() {
    historicoRef.current.scrollToTop();
  }

  function closeWarningDialog() {
    setWarningDialog({ ...warningDialog, open: false });
  }

  const findHistoricosByFilters = useCallback(async (formElement, dataCadastro) => {
    setLoadings(loadingController.changeLoadingHistorico(true));
    const data = await controller.findHistoricosWhenSubmit(processo.id, formElement, dataCadastro, errors);
    if (data.error) {
      setErrors(data.error);
    }
    setHistoricos(data.historicos);
    historicoRef.current.scrollToTop();
    setLoadings(loadingController.changeLoadingHistorico(false));
  }, [processo, errors, setLoadings]);

  async function onReset() {
    setVisualizarDataCadastramento({ key: uuid(), value: false });
    setDataCadastramento('');
    setErrors(controller.makeDefaultHistoricoSearchErrors());
    setDropdowns(controller.makeDefaultDropdownHistorico());
    setHistoricos(await controller.findAcompanhementWhenReset(processo.id));
    historicoRef.current.scrollToTop();
  }

  async function onSubmit(e) {
    e.preventDefault();
    await findHistoricosByFilters(e.target, dataCadastramento);
  }

  async function addNextHistoricos() {
    setLoadingHistorico(true);
    setHistoricos(await controller.findHistoricoWhenScrolled(processo.id, historicos));
    setLoadingHistorico(false);
  }


  async function onCheckedHistorico(e) {
    e.persist();
    setLoadings(loadingController.changeLoadingHistorico(true));
    const { name: idProcessoHistorico, checked } = e.target;
    const updateReturn = await controller.udpateHistoricosWhenChecked(processo.id, idProcessoHistorico, checked);
    if (updateReturn.error) {
      setWarningDialog({ open: true, message: updateReturn.error });
    } else {
      historicoRef.current.scrollToTop();
      setHistoricos(updateReturn.historicos);
    }
    e.target.checked = updateReturn.checked;
    setLoadings(loadingController.changeLoadingHistorico(false));
  }

  const onChangetVisualizarData = useCallback(async (e) => {
    const { checked } = e.target;
    let data = '';
    if (checked) {
      data = controller.makeDefaultDataCadastramento(numeroDias);
    }
    setDataCadastramento(data);
    setVisualizarDataCadastramento({ ...visualizarDataCadastramento, value: checked });
    await findHistoricosByFilters(formRef.current, data);
  }, [numeroDias, visualizarDataCadastramento, findHistoricosByFilters]);

  const onChangeData = useCallback((e) => {
    setDataCadastramento(e.target.value);
  }, []);

  const onChangeDropdown = useCallback((e) => {
    const { name, value } = e.target;
    setDropdowns(old => ({ ...old, [name]: value }));
  }, []);

  const clearError = useCallback((e) => {
    const { name } = e.target;
    setErrors(clearErrorField(errors, name));
  }, [errors]);

  useImperativeHandle(ref, () => ({
    moveScrollToTop,
  }));

  useLayoutEffect(() => {
    setDataCadastramento('');
  }, [numeroDias]);

  useEffect(() => {
    if (!processo.id || !numeroDias) {
      return;
    }

    setLoadings(loadingController.changeLoadingHistorico(true));
    controller.findHistoricosWhenSubmit(processo.id, formRef.current, dataCadastramento)
      .then((res) => {
        setHistoricos(res.historicos);
      }).catch(() => {
        setHistoricos({});
      }).finally(() => {
        setLoadings(loadingController.changeLoadingHistorico(false));
      });
  }, [processo, numeroDias, setLoadings, dataCadastramento]);

  return (
    <>
      <DialogNew
        open={onOpenExpandirHistorico}
        text="Histórico"
        closeIcon
        onClose={() => setOnOpenExpandirHistorico(false)}
        PaperProps={{ style: { width: '95vw', maxWidth: '95vw' } }}
      >
        {onOpenExpandirHistorico && (
          <DialogExpandirHistorico
            idProcesso={processo.id}
            historicos={historicos}
            showLoading={loadingHistorico}
          />
        )}
      </DialogNew>
      <AlertDialog
        open={warningDialog.open}
        variant="warning"
        onClickConfirm={closeWarningDialog}
      >
        <Typography>{warningDialog.message}</Typography>
      </AlertDialog>
      <Form onReset={onReset} onSubmit={onSubmit} ref={formRef}>
        <FieldsContainer>
          <Grid24 container spacing="1">
            <VisualizarAPartirDeContainer container item xs={24} sm={9}>
              <div style={{ width: '131px' }}>
                <CheckboxUI
                  key={visualizarDataCadastramento.key}
                  checked={visualizarDataCadastramento.value}
                  label="Visualizar a partir de"
                  onChange={onChangetVisualizarData}
                />
              </div>
              <div style={{ width: 'calc(100% - 131px)' }}>
                <DateField
                  isForm
                  maxDate={new Date()}
                  fullWidth
                  disabled={!visualizarDataCadastramento || dataCadastramento === ''}
                  name="dataCadastramento"
                  value={dataCadastramento}
                  error={errors.dataCadastramento.error}
                  errorMessage={errors.dataCadastramento.errorMessage}
                  onChange={onChangeData}
                  onFocus={clearError}
                />
              </div>
            </VisualizarAPartirDeContainer>
            <Grid24 container item xs={24} sm={7}>
              <SelectorField
                hasEmptyLabel
                fullWidth
                label="Tipos de Histórico"
                items={tiposHistorico}
                name="idTipoHistorico"
                value={dropdowns.idTipoHistorico}
                onChange={onChangeDropdown}
              />
            </Grid24>
            <Grid24 container item xs={24} sm={8}>
              <SelectorField
                hasEmptyLabel
                fullWidth
                label="Fase"
                items={fases}
                name="idFase"
                value={dropdowns.idFase}
                onChange={onChangeDropdown}
              />
            </Grid24>
          </Grid24>
          <BottomGrid container spacing="1">
            <Grid24 container item xs={24} sm={16}>
              <SelectorField
                hasEmptyLabel
                fullWidth
                label="Autor"
                items={autores}
                name="idUsuario"
                value={dropdowns.idUsuario}
                onChange={onChangeDropdown}
              />
            </Grid24>
            <Grid24 container item xs={24} sm={8}>
              <InputField
                fullWidth
                label="Conteúdo"
                name="conteudo"
              />
            </Grid24>
          </BottomGrid>
        </FieldsContainer>
        <ActionsButtons>
          <SearchButton type="submit">
            <FaSearch size={18} />
          </SearchButton>
          <ClearButtonHistorico type="reset" variant="outlined">
            <FaEraser size={18} />
            <span>Limpar</span>
          </ClearButtonHistorico>
          <TooltipUI title="Expandir histórico">
            <ExpandButton variant="outlined" onClick={() => setOnOpenExpandirHistorico(true)}>
              <OpenInNewIcon fontSize="small" />
              <span>Expandir</span>
            </ExpandButton>
          </TooltipUI>
        </ActionsButtons>
      </Form>
      <HistoricoGrid>
        <HistoricoProcesso
          ref={historicoRef}
          isDisbaledChangePin={!podeModificarFase}
          showLoading={loadingHistorico}
          historicos={historicos}
          addNextHistoricos={addNextHistoricos}
          onChangeChecked={onCheckedHistorico}
        />
      </HistoricoGrid>
    </>
  );
}
export const Historico = React.memo(React.forwardRef(HistoricoComponent));
